import { Component, computed, effect, EffectRef, ElementRef, inject, Signal, ViewChild } from '@angular/core';
import { Configuration } from '../../../../models/interfaces/configuration/index.interface';
import { APP_CONFIG } from '../../../../configuration/configuration';
import { ButtonComponent } from '../../../shared/button/button.component';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import { DvmService } from '@services/dvm.service';
import { ActionButton, ButtonType, ModalActionButton } from '@models/types/components/ActionButton.type';
import { LayoutService } from '@services/layout.service';
import anime from 'animejs';
import { MobileButtonService } from '@services/components/mobile-button.service';
import { Dialog } from '@angular/cdk/dialog';
import { ModalButtonDialogComponent } from '@components/minisite/dialogs/modal-button-dialog/modal-button-dialog.component';

@Component({
  selector: 'app-link-action-buttons',
  standalone: true,
  imports: [
    ButtonComponent
  ],
  templateUrl: './action-buttons.component.html',
  styleUrl: './action-buttons.component.css'
})
export class ActionButtonsComponent {

  @ViewChild('actionButtonsContainer')
  private readonly actionButtonsContainer!: ElementRef<HTMLDivElement>;

  // SERVICES
  private readonly mvlBtnService: MobileButtonService    = inject(MobileButtonService);
  private readonly dialog = inject(Dialog);

  // STATE
  private readonly view:          Signal<AppViews|null>  = inject(DvmService).view;
  private readonly isMobile:      Signal<boolean>        = inject(LayoutService).isMobile;

  // CONFIGURATION
  private readonly configuration: Signal<Configuration>  = inject(APP_CONFIG);
  public readonly actionButtons: Signal<ActionButton[]> = computed((): ActionButton[] => {
    // Merge LinkActionButtons for reverse compatibility
    const merged: ActionButton[] = [
      ...(this.configuration().Ui.ActionButtons ?? []),
      ...(this.configuration().Ui.LinkActionButtons ?? []),
    ];
    return merged;

  });
  
  protected readonly ButtonType = ButtonType;

  // COMPONENT STATE
  public           showElement:   boolean                = false;
  private readonly viewChange:    EffectRef              = effect((): void => {

    if(this.isMobile()) return;
    
    const view: AppViews | null = this.view(),
          show: boolean         = view ? view !== AppViews.Pano : false;

    if(view === null) return;

    this.toggleComponentVisibility(show);
   
  })

  private readonly mobileBtnChange: EffectRef = effect((): void => {

    const isMobile = this.isMobile();

    if (!isMobile) return;

    const active: boolean = this.mvlBtnService.active();

    this.toggleComponentVisibility(active);

  })

  // METHODS
  public handleActionButton(button: ActionButton): void {
    if (button.buttonType === ButtonType.Url) {
      window.open(button.url, '_blank');
    } else if (button.buttonType === ButtonType.Modal) {
      this.openModal(button);
    }
  }

  private openModal(actionButton: ModalActionButton): void {
    this.dialog.open(ModalButtonDialogComponent, {
      data: { actionButton },
      width: '500px',
    });
  }

  public openExternalLink(url: string): void {
    window.open(url, '_blank');
  }

  private toggleComponentVisibility(show: boolean): void {
    if(!this.showElement && show) this.showElement = true;

    anime({
      targets:  this.actionButtonsContainer.nativeElement,
      opacity:  show ? 1 : 0,
      easing:   'easeInOutQuad',
      duration: 300,
      complete: (): void => { if(!show) this.showElement = false }
    })

  }

}
